import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '../ui/Card'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { 
  Clock, 
  Flame, 
  Leaf, 
  AlertCircle,
  Plus,
  Minus,
  Heart,
  Info,
  ShieldCheck,
  Merge
} from 'lucide-react'
import { Meal } from '../../services/mealmateService'
import NutritionInfo from './NutritionInfo'
import IngredientTransparency from './IngredientTransparency'
import SynergyAnalysis from './SynergyAnalysis'
import AIVisual from '../ui/AIVisual'

interface MealCardProps {
  meal: Meal
  onAddToCart: (meal: Meal, quantity: number) => void
  dietaryFilters: string[]
}

const MealCard: React.FC<MealCardProps> = ({ meal, onAddToCart, dietaryFilters }) => {
  const [quantity, setQuantity] = useState(1)
  const [showNutrition, setShowNutrition] = useState(false)
  const [showIngredients, setShowIngredients] = useState(false)
  const [showSynergy, setShowSynergy] = useState(false)
  const [isFavorited, setIsFavorited] = useState(false)

  const getSpiceLevelColor = (level: string) => {
    switch (level) {
      case 'mild': return 'bg-green-100 text-green-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'hot': return 'bg-red-100 text-red-800'
      default: return 'bg-theme-surface-light text-theme-text-primary'
    }
  }

  const getDietaryBadgeColor = (tag: string) => {
    const colors: Record<string, string> = {
      'vegetarian': 'bg-green-100 text-green-800',
      'vegan': 'bg-emerald-100 text-emerald-800',
      'gluten-free': 'bg-orange-100 text-orange-800',
      'keto': 'bg-purple-100 text-purple-800',
      'paleo': 'bg-amber-100 text-amber-800',
      'low-carb': 'bg-blue-100 text-blue-800',
      'high-protein': 'bg-indigo-100 text-indigo-800',
      'dairy-free': 'bg-pink-100 text-pink-800',
      'organic': 'bg-lime-100 text-lime-800'
    }
    return colors[tag.toLowerCase()] || 'bg-theme-surface-light text-theme-text-primary'
  }

  const getCategoryBadgeColor = (category: string) => {
    const colors: Record<string, string> = {
      'breakfast': 'bg-yellow-100 text-yellow-800',
      'lunch': 'bg-blue-100 text-blue-800',
      'dinner': 'bg-purple-100 text-purple-800',
      'snack': 'bg-orange-100 text-orange-800',
      'dessert': 'bg-pink-100 text-pink-800',
      'appetizer': 'bg-teal-100 text-teal-800',
      'soup': 'bg-cyan-100 text-cyan-800',
      'salad': 'bg-green-100 text-green-800'
    }
    return colors[category.toLowerCase()] || 'bg-theme-surface-light text-theme-text-primary'
  }

  const matchesDietaryFilters = () => {
    if (dietaryFilters.length === 0) return true
    return dietaryFilters.some(filter => 
      meal.dietaryTags.some(tag => tag.toLowerCase().includes(filter.toLowerCase()))
    )
  }

  const getCalorieDensity = () => {
    if (meal.nutritionInfo.calories < 300) return 'Low Calorie'
    if (meal.nutritionInfo.calories < 500) return 'Moderate'
    if (meal.nutritionInfo.calories < 700) return 'High Calorie'
    return 'Very High Calorie'
  }

  const handleAddToCart = () => {
    onAddToCart(meal, quantity)
    setQuantity(1) // Reset quantity after adding
  }

  const incrementQuantity = () => {
    setQuantity(prev => prev + 1)
  }

  const decrementQuantity = () => {
    setQuantity(prev => Math.max(1, prev - 1))
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.3 }}
      className={`${!matchesDietaryFilters() ? 'opacity-60' : ''}`}
    >
      <Card className="h-full hover:shadow-lg transition-all duration-300 border-2 hover:border-green-200 group">
        {/* Meal Image */}
        <div className="relative">
          <div className="h-48 bg-gradient-to-br from-green-100 to-emerald-100 rounded-t-xl overflow-hidden">
            <AIVisual
              src={meal.image}
              alt={meal.name}
              fallbackType="food"
              className="w-full h-full"
            />
            
            {/* Dietary Match Indicator */}
            {matchesDietaryFilters() && dietaryFilters.length > 0 && (
              <div className="absolute top-3 left-3 bg-green-500 text-white rounded-full p-2">
                <Leaf className="w-4 h-4" />
              </div>
            )}

            {/* Availability Status */}
            {!meal.isAvailable && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <Badge className="bg-red-500 text-white">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Currently Unavailable
                </Badge>
              </div>
            )}

            {/* Spice Level Indicator */}
            <div className="absolute top-3 right-3">
              <Badge className={`${getSpiceLevelColor(meal.spiceLevel)} text-xs`}>
                <Flame className="w-3 h-3 mr-1" />
                {meal.spiceLevel}
              </Badge>
            </div>

            {/* Category Badge */}
            <div className="absolute bottom-3 left-3">
              <Badge className={`${getCategoryBadgeColor(meal.category)} text-xs`}>
                {meal.category}
              </Badge>
            </div>

            {/* Prep Time */}
            <div className="absolute bottom-3 right-3 bg-theme-surface/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center space-x-1">
              <Clock className="w-3 h-3 text-theme-text-secondary" />
              <span className="text-xs font-medium text-theme-text-primary">{meal.prepTime}min</span>
            </div>
          </div>

          {/* Favorite Button */}
          <button
            onClick={() => setIsFavorited(!isFavorited)}
            className="absolute top-3 right-3 p-2 bg-theme-surface/90 backdrop-blur-sm rounded-full hover:bg-theme-surface transition-colors"
          >
            <Heart 
              className={`w-4 h-4 ${isFavorited ? 'text-red-500 fill-current' : 'text-theme-text-muted'}`}
            />
          </button>
        </div>

        <CardContent className="p-4">
          {/* Meal Name and Price */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-theme-text-primary mb-1 line-clamp-2">{meal.name}</h3>
              <p className="text-sm text-theme-text-secondary line-clamp-2">{meal.description}</p>
            </div>
            <div className="text-right ml-3">
              <div className="text-xl font-bold text-green-600">${meal.price}</div>
              <div className="text-xs text-theme-text-muted">{getCalorieDensity()}</div>
            </div>
          </div>

          {/* Nutrition Summary */}
          <div className="flex items-center justify-between mb-3 p-2 bg-theme-surface rounded-lg">
            <div className="flex items-center space-x-4 text-xs text-theme-text-secondary">
              <div className="text-center">
                <div className="font-semibold text-theme-text-primary">{meal.nutritionInfo.calories}</div>
                <div>calories</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-theme-text-primary">{meal.nutritionInfo.protein}g</div>
                <div>protein</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-theme-text-primary">{meal.nutritionInfo.carbs}g</div>
                <div>carbs</div>
              </div>
            </div>
            
            <div className="flex gap-1">
              <button
                onClick={() => {
                  setShowIngredients(!showIngredients)
                  setShowNutrition(false)
                  setShowSynergy(false)
                }}
                className={`p-1 transition-colors ${showIngredients ? 'text-theme-primary' : 'text-theme-text-muted hover:text-theme-text-primary'}`}
                title="View Ingredient Transparency"
              >
                <ShieldCheck className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setShowSynergy(!showSynergy)
                  setShowIngredients(false)
                  setShowNutrition(false)
                }}
                className={`p-1 transition-colors ${showSynergy ? 'text-theme-primary' : 'text-theme-text-muted hover:text-theme-text-primary'}`}
                title="View Nutrient Synergy"
              >
                <Merge className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setShowNutrition(!showNutrition)
                  setShowIngredients(false)
                  setShowSynergy(false)
                }}
                className={`p-1 transition-colors ${showNutrition ? 'text-theme-primary' : 'text-theme-text-muted hover:text-theme-text-primary'}`}
                title="View Nutrition Info"
              >
                <Info className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Dietary Tags */}
          {meal.dietaryTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {meal.dietaryTags.slice(0, 3).map((tag) => (
                <Badge
                  key={tag}
                  className={`${getDietaryBadgeColor(tag)} text-xs px-2 py-1 flex items-center space-x-1`}
                >
                  <Leaf className="w-3 h-3" />
                  <span>{tag}</span>
                </Badge>
              ))}
              {meal.dietaryTags.length > 3 && (
                <Badge className="bg-theme-surface text-theme-text-secondary text-xs px-2 py-1">
                  +{meal.dietaryTags.length - 3}
                </Badge>
              )}
            </div>
          )}

          {/* Allergen Warnings */}
          {meal.allergens.length > 0 && (
            <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center space-x-2 text-xs text-amber-800">
                <AlertCircle className="w-3 h-3" />
                <span>Contains: {meal.allergens.join(', ')}</span>
              </div>
            </div>
          )}

          {/* Nutrition Details */}
          {showNutrition && (
            <div className="mb-4">
              <NutritionInfo nutrition={meal.nutritionInfo} />
            </div>
          )}

          {/* Ingredient Transparency Layer */}
          {showIngredients && meal.detailedIngredients && meal.dietaryClaims && (
            <div className="mb-4">
              <IngredientTransparency 
                ingredients={meal.detailedIngredients} 
                claims={meal.dietaryClaims} 
              />
            </div>
          )}

          {/* Synergy Analysis Layer */}
          {showSynergy && meal.synergyAnalysis && (
            <div className="mb-4">
              <SynergyAnalysis synergy={meal.synergyAnalysis} />
            </div>
          )}

          {/* Add to Cart Controls */}
          <div className="flex items-center justify-between space-x-3">
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                variant="outline"
                onClick={decrementQuantity}
                disabled={quantity <= 1}
                className="w-8 h-8 p-0"
              >
                <Minus className="w-3 h-3" />
              </Button>
              
              <span className="w-8 text-center font-semibold text-theme-text-primary">{quantity}</span>
              
              <Button
                size="sm"
                variant="outline"
                onClick={incrementQuantity}
                className="w-8 h-8 p-0"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
            
            <Button
              onClick={handleAddToCart}
              disabled={!meal.isAvailable}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-theme-surface"
            >
              Add to Cart
            </Button>
          </div>

          {/* Dietary Match Indicator */}
          {matchesDietaryFilters() && dietaryFilters.length > 0 && (
            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2 text-xs text-green-800">
                <Leaf className="w-3 h-3" />
                <span>Matches your dietary preferences</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  )
}

export default MealCard